import { useQuery } from '@tanstack/react-query'
import { fetchExperiment } from './api'
const moment = require('moment')

const tryValidateSubscriptionConfig = (config) => {
  return (config?.paymentType === 'SUBSCRIPTION')
    ? (!!config?.billingPlanPublicId && !!config?.billingPlanType)
    : true
}

const checkIfActive = (experiment) => {
  return (
    experiment?.experimentType === 'BOOK_AND_JOIN' &&
    experiment?.isEnabled &&
    new Date() <= new Date(experiment?.config?.endDate) &&
    tryValidateSubscriptionConfig(experiment?.config)
  )
}

/*
const generateBookingSlots = (config) => {
  if (!config) return []

  const slots = []
  const startDate = moment(config.startDate)
  const endDate = moment(config.endDate)
  const duration = config.appointmentDurationInMinutes || 30
  const includeWeekends = config.includeWeekends ?? false

  for (let date = startDate.clone(); date.isSameOrBefore(endDate, 'day'); date.add(1, 'day')) {
    const dayOfWeek = date.day()

    if (!includeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) continue

    const dayStart = moment(`${date.format('YYYY-MM-DD')}T${config.startTime}`)
    const dayEnd = moment(`${date.format('YYYY-MM-DD')}T${config.endTime}`)

    for (let slotStart = dayStart.clone(); slotStart.isBefore(dayEnd); slotStart.add(duration, 'minutes')) {
      slots.push({
        localDate: date.format('YYYY-MM-DD'),
        startDateTimeLocal: slotStart.format('YYYY-MM-DDTHH:mm:ss')
      })
    }
  }

  return randomizeSlots(slots, config.locationIds)
}
*/

const shuffleArray = (array, seed) => {
  const shuffled = [...array]
  let random = seed

  for (let i = shuffled.length - 1; i > 0; i--) {
    random = (random * 9301 + 49297) % 233280
    const j = Math.floor((random / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }

  return shuffled
}

const randomizeSlots = (slots, locationIds = []) => {
  const MIN_SLOTS = 1
  const MAX_SLOTS = 5
  const slotsByDate = {}

  const locationSeed = locationIds.join('').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)

  slots.forEach(slot => {
    if (!slotsByDate[slot.localDate]) slotsByDate[slot.localDate] = []
    slotsByDate[slot.localDate].push(slot)
  })

  const randomized = []

  Object.keys(slotsByDate).forEach(date => {
    const daySlots = slotsByDate[date]
    const dateSeed = parseInt(date.split('-').join(''))
    const seed = dateSeed + locationSeed
    const shuffled = shuffleArray(daySlots, seed)

    const countSeed = ((seed * 48271) % 2147483647) * ((seed % 999) + 1)
    const normalized = (countSeed % 100000) / 100000
    const count = MIN_SLOTS + Math.floor(normalized * (MAX_SLOTS - MIN_SLOTS + 1))
    const finalCount = Math.min(count, shuffled.length)

    randomized.push(...shuffled.slice(0, finalCount).sort((a, b) =>
      a.startDateTimeLocal.localeCompare(b.startDateTimeLocal)
    ))
  })

  return randomized
}

export const generateBookingSlotsForLocation = (config, locationId) => {
  if (!config) return []

  const slots = []
  const startDate = moment(config.startDate)
  const endDate = moment(config.endDate)
  const duration = config.appointmentDurationInMinutes || 30
  const includeWeekends = config.includeWeekends ?? false

  for (let date = startDate.clone(); date.isSameOrBefore(endDate, 'day'); date.add(1, 'day')) {
    const dayOfWeek = date.day()

    if (!includeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) continue

    const dayStart = moment(`${date.format('YYYY-MM-DD')}T${config.startTime}`)
    const dayEnd = moment(`${date.format('YYYY-MM-DD')}T${config.endTime}`)

    for (let slotStart = dayStart.clone(); slotStart.isBefore(dayEnd); slotStart.add(duration, 'minutes')) {
      slots.push({
        localDate: date.format('YYYY-MM-DD'),
        startDateTimeLocal: slotStart.format('YYYY-MM-DDTHH:mm:ss')
      })
    }
  }

  return randomizeSlots(slots, [locationId])
}

export const useFetchExperiment = ({ externalId }) => {
  const { data: experiment, isLoading, isRefetching } = useQuery({
    queryKey: ['fetchExperiment', externalId],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300))
      return await fetchExperiment({ externalId })
    },
    enabled: !!externalId,
    retry: 3
  })

  return {
    experiment,
    isLoadingExperiment: isLoading || isRefetching,
    isExperimentActive: checkIfActive(experiment)
  }
}
